import React, {
  useState,
  useEffect,
  useRef
} from 'react';
import { useStreamData } from '@lib/hooks/useStreamData';
import { pushDataLayerForEvent } from '@lib/gtag';
import mapboxgl from 'mapbox-gl';
import ReactDOM from 'react-dom/client';
import 'mapbox-gl/dist/mapbox-gl.css';
import { hexToRGB } from '@services/utils.service';
import { mapStylesMap, parseColorToHex } from '@services/interactions.service';

import PlaceDetailsPopup from './PlaceDetailsPopup';
import CustomMapMarker from './CustomMapMarker';
import { getMapPopupDetails, getMapPopupSummary } from '@services/presentation-live';
import useInteractionSound from '@components/Presentation/hooks/useInteractionSound';

const LAYERS_TO_CHECK = ['clusters', 'unclustered-point', 'unclustered-point-hitarea', 'newpoints'];

const MapBoxComponent = ({
  interactionState,
  mapId,
  colors,
  settings,
  mapData,
  currentRosResult = [],
  height,
  isMuted
}) => {
  mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
  const mapContainerRef = useRef(null);
  const map = useRef(null);
  const [point, setPoint] = useState([]);
  const pointsRef = useRef([]);
  const newPointsRef = useRef([]);
  const viewRegion = { center: [29, 9.88], zoom: 1.3 };
  const mapHeight = height ? '100%' : (mapData?.height ? mapData.height : '100%');
  const { mapBox } = useStreamData(s => ({ mapBox: s.mapBox }));
  const placeInfo = useRef({});
  const popupRootRef = useRef(null);
  const placeDetailsPrefetchRequestedRef = useRef(new Set());
  const placeDetailsPrefetchQueueRef = useRef([]);
  const placeDetailsPrefetchTimerRef = useRef(null);
  const currentPopupPlaceRef = useRef(null);
  const oneUoneVRef = useRef(settings?.mapConfig?.oneUoneV || false);
  const markersRef = useRef(new Map());
  const eventHandlersRef = useRef([]);
  const { playSound, stopSound } = useInteractionSound({
    featureSettings: { settings, type: 'map' },
    soundKey: 'mapSoundOptions',
    isEnabledKey: 'mapSound',
    isMuted,
    options: {
      volume: 0.5,
      playbackRate: 0.75
    }
  });

  const mapboxStyles = {
    Standard: 'mapbox://styles/mapbox/streets-v12',
    Light: 'mapbox://styles/sa-techassets/cm6p63upe002l01qr6mmff01a',
    Dark: 'mapbox://styles/sa-techassets/cm6p669lt00sl01qq8c2phu78',
    Sky: 'mapbox://styles/sa-techassets/cm6p67nxz00rj01ry66zrajr8',
    Globe: 'mapbox://styles/mapbox/streets-v12',
    Comic: 'mapbox://styles/sa-techassets/cm2xfw6d7005501qz3e4sd0ke',
    Pencil: 'mapbox://styles/sa-techassets/cm2xeq2s8004y01qt0ifadj6o',
    Blueprint: 'mapbox://styles/sa-techassets/cm2xeq3i500jm01pa9l8kgwdq',
    Bright: 'mapbox://styles/sa-techassets/cm2xejuij004x01qzb6qk3614',
    'Bright (Globe)': 'mapbox://styles/sa-techassets/cm2xen9jo00qy01pm3plb3ds1',
  };

  const mapboxStyle = settings?.mapConfig?.style || 'Standard';
  const mapboxStyleURL = mapboxStyles[mapboxStyle];

  function groupBy(array, keyFn) {
    return array.reduce(function (result, item) {
      let key = keyFn(item);
      if (key == null || key === '') {
        console.warn('Skipping item with null/undefined/empty key:', item);
        return result;
      }
      if (!result[key]) result[key] = [];
      result[key].push(item);
      return result;
    }, Object.create(null));
  }

  const popupRef = useRef(new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: true,
    className: 'mapbox-popup',
  }));

  const isMapValid = (mapInstance = map.current) => {
    return mapInstance &&
      !mapInstance?._removed &&
      mapInstance?.getContainer() &&
      mapInstance?.getContainer().parentNode;
  };

  const getValidMap = () => {
    if (!isMapValid()) {
      console.warn('Map instance is invalid or removed');
      return null;
    }
    return map.current;
  };

  function paintMapLayers(mapInstance) {
    // Uncomment if you want to see the layers in the console
    // console.log('mapbox: layers:', mapInstance?.getStyle());

    if (!mapInstance || !mapboxStyle || !colors) return;

    const mapLayers = mapStylesMap[mapboxStyle];
    if (!mapLayers) return;

    if (!mapInstance?.isStyleLoaded()) {
      mapInstance?.once('styledata', () => paintMapLayers(mapInstance));
      return;
    }

    try {
      mapLayers.water?.forEach(([name, property]) => mapInstance?.setPaintProperty(name, property, colors.mapWaterColor));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Map.js load waterColor style has error!', err);
    }
    try {
      mapLayers.land?.forEach(([name, property]) => mapInstance?.setPaintProperty(name, property, colors.mapLandColor));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Map.js load landColor style has error!', err);
    }
  }

  // Clears data only. Layers, sources and event handlers must survive a clean:
  // removing them here is what used to kill click/popup handling, because
  // updateMap() only re-adds sources, never layers or handlers. Layers are
  // only rebuilt on style changes (updateMapSourcesAndLayers).
  const cleanUp = () => {
    const mapInstance = getValidMap();
    if (!mapInstance) return;

    setPoint([]);
    pointsRef.current = [];
    newPointsRef.current = [];

    removeAllMarkers();

    if (mapInstance?.getSource('newPoint')) {
      mapInstance?.getSource('newPoint').setData({
        type: 'FeatureCollection',
        features: []
      });
    }

    if (mapInstance?.getSource('point')) {
      mapInstance?.getSource('point').setData({
        type: 'FeatureCollection',
        features: []
      });
    }
  };

  // Place details (Google Place/timezone/weather lookups) are prefetched
  // (one place per second) as soon as points land on the map instead of when
  // a point is clicked. The backend caches the result in Redis; by the time
  // someone clicks, the popup-details call is a cache hit. The summary is
  // not prefetched — it loads when the popup is opened.
  const PLACE_DETAILS_PREFETCH_INTERVAL_MS = 1000;

  const drainPlaceDetailsPrefetchQueue = () => {
    const next = placeDetailsPrefetchQueueRef.current.shift();
    if (!next) {
      placeDetailsPrefetchTimerRef.current = null;
      return;
    }
    getMapPopupDetails(next.place, next.placeType).catch(() => { /* noop — prefetch only */ });
    placeDetailsPrefetchTimerRef.current = setTimeout(drainPlaceDetailsPrefetchQueue, PLACE_DETAILS_PREFETCH_INTERVAL_MS);
  };

  const queuePlaceDetailsPrefetch = (features) => {
    (features || []).forEach((feature) => {
      const place = feature?.properties?.place;
      if (!place || placeDetailsPrefetchRequestedRef.current.has(place)) return;
      placeDetailsPrefetchRequestedRef.current.add(place);
      placeDetailsPrefetchQueueRef.current.push({ place, placeType: feature.properties.placeType });
    });
    if (!placeDetailsPrefetchTimerRef.current && placeDetailsPrefetchQueueRef.current.length) {
      drainPlaceDetailsPrefetchQueue();
    }
  };

  // While another client's generation is in flight the endpoint returns an
  // empty summary, so poll a few times before giving up.
  const SUMMARY_POLL_INTERVAL_MS = 2000;
  const SUMMARY_POLL_MAX_ATTEMPTS = 5;

  const fetchSummaryWithRetry = async (placeName, placeType) => {
    for (let attempt = 0; attempt < SUMMARY_POLL_MAX_ATTEMPTS; attempt++) {
      try {
        const data = await getMapPopupSummary(placeName, placeType);
        if (data?.entity?.summary) return data.entity.summary;
      } catch { /* retry */ }
      await new Promise(resolve => setTimeout(resolve, SUMMARY_POLL_INTERVAL_MS));
    }
    return '';
  };

  const loadSummaryIntoPopup = async (place) => {
    const summary = await fetchSummaryWithRetry(place.placeName, place.placeType);
    const cached = placeInfo.current[place.placeName];
    if (cached?.placeDetails && summary) cached.placeDetails.summary = summary;

    // Only re-render if the popup is still showing this place
    if (currentPopupPlaceRef.current !== place.placeName || !popupRootRef.current) return;
    const updatedPlace = placeInfo.current[place.placeName] || place;
    popupRootRef.current.render(
      <PlaceDetailsPopup
        isFetching={false}
        isFetchingSummary={false}
        place={updatedPlace}
        onPhotoError={() => refreshPlaceDetails(updatedPlace)}
      />
    );
  };

  const renderPlacePopup = async (features) => {
    if (!features || features.length === 0) {
      console.warn('No features provided to renderPlacePopup');
      return;
    }

    const htmlStr = `<div class="w-[220px] ${features[0].properties.placeType === 11 ? '' : 'min-h-52'} flex flex-col" id="place-details-popup"></div>`;

    const { coordinates } = features[0].geometry;
    popupRef.current.setLngLat(coordinates).setHTML(htmlStr).addTo(map.current);

    const popupContainer = document.querySelector('#place-details-popup');
    if (!popupContainer) return;

    popupRootRef.current = ReactDOM.createRoot(popupContainer);
    popupRootRef.current.render(
      <PlaceDetailsPopup
        isFetching={true}
      />
    );

    const places = groupBy(features, ({ properties }) => properties.place);

    const fetchPromises = Object.entries(places)
      .map(([place, featureGroup]) => fetchAndCachePlaceDetails(place, featureGroup));

    await Promise.all(fetchPromises);

    const place = placeInfo.current[features[0].properties.place];
    currentPopupPlaceRef.current = features[0].properties.place;

    const isSummaryMissing = !!place && !place.isError && !place.placeDetails?.summary;

    popupRootRef.current.render(
      <PlaceDetailsPopup
        isFetching={false}
        isFetchingSummary={isSummaryMissing}
        place={place}
        onPhotoError={() => refreshPlaceDetails(place)}
      />
    );

    if (isSummaryMissing) loadSummaryIntoPopup(place);
  };

  const refreshPlaceDetails = async (existingPlace) => {
    if (!existingPlace?.placeName) return;
    if (popupRootRef.current) {
      popupRootRef.current.render(
        <PlaceDetailsPopup
          isFetching={false}
          isRefreshingPhotos={true}
          place={existingPlace}
        />
      );
    }
    try {
      const data = await getMapPopupDetails(existingPlace.placeName, existingPlace.placeType, true);
      const isError = !data.status || !data.entity?.displayName;
      const photoUrls = (data.entity.photos || []).map(photo =>
        `${process.env.NEXT_PUBLIC_API_URL}image-proxy?url=${encodeURIComponent(photo.split('&key=')[0])}`
      );
      placeInfo.current[existingPlace.placeName] = {
        ...existingPlace,
        placeDetails: isError ? {} : { ...data.entity, photos: photoUrls },
        isError
      };
      if (popupRootRef.current) {
        popupRootRef.current.render(
          <PlaceDetailsPopup
            isFetching={false}
            place={placeInfo.current[existingPlace.placeName]}
          />
        );
      }
    } catch {
      if (popupRootRef.current) {
        popupRootRef.current.render(
          <PlaceDetailsPopup
            isFetching={false}
            place={existingPlace}
          />
        );
      }
    }
  };

  const trackedOn = (mapInstance, event, layer, handler) => {
    if (layer) {
      mapInstance.on(event, layer, handler);
    } else {
      mapInstance.on(event, handler);
    }
    eventHandlersRef.current.push({ event, layer, handler });
  };

  function setupMapEventHandlers(mapInstance) {
    // Clean up existing listeners first
    cleanupMapEventHandlers(mapInstance);

    const setCursorPointer = () => {
      if (!mapInstance) return;
      mapInstance.getCanvas().style.cursor = 'pointer';
    };
    const clearCursor = () => {
      if (!mapInstance) return;
      mapInstance.getCanvas().style.cursor = '';
    };

    const clusterClickHandler = (e) => {
      const features = mapInstance?.queryRenderedFeatures(e.point, { layers: ['clusters'] });
      if (features && features.length > 0) {
        const clusterId = features[0].properties.cluster_id;
        mapInstance?.getSource('point').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          mapInstance?.easeTo({ center: features[0].geometry.coordinates, zoom });
        });
      }
    };
    trackedOn(mapInstance, 'click', 'clusters', clusterClickHandler);

    trackedOn(mapInstance, 'mouseenter', 'points', setCursorPointer);
    trackedOn(mapInstance, 'mouseleave', 'points', clearCursor);

    const handleUnclusteredPointClick = (e) => {
      const bbox = [
        [e.point.x - 20, e.point.y - 20],
        [e.point.x + 20, e.point.y + 20],
      ];
      const clickFeatures = mapInstance?.queryRenderedFeatures(bbox, {
        layers: ['unclustered-point', 'unclustered-point-hitarea'],
      });
      if (clickFeatures && clickFeatures.length > 0) {
        renderPlacePopup(clickFeatures);
      }
    };
    trackedOn(mapInstance, 'click', ['unclustered-point', 'unclustered-point-hitarea'], handleUnclusteredPointClick);
    trackedOn(mapInstance, 'mouseleave', 'unclustered-point', clearCursor);

    trackedOn(mapInstance, 'mouseenter', 'clusters', setCursorPointer);
    trackedOn(mapInstance, 'mouseleave', 'clusters', clearCursor);
  }

  const initializeMap = (mapInstance, features) => {
    if (!mapInstance) return;
    paintMapLayers(mapInstance);

    addPointSource(mapInstance, mapData?.features);

    mapInstance?.addLayer({
      id: 'points',
      type: 'symbol',
      source: 'point'
    });

    if (features.length > 0) {
      const places = groupBy(features, ({ properties }) => properties.place);
      const latestPlace = features[features.length - 1]?.properties?.place;

      renderMarkers(mapInstance, places, latestPlace);
      queuePlaceDetailsPrefetch(features);
    }

    addClustersLayer(mapInstance);
    // addClusterCountLayer(mapInstance);
    addUnclusteredPointLayer(mapInstance);
    addNewPointSource(mapInstance, []);
    addNewPointsLayer(mapInstance);

    // Ensure all layers are ready before setting up event handlers
    const checkAndSetupEvents = () => {
      const allLayersReady = LAYERS_TO_CHECK.every(layerId => mapInstance?.getLayer(layerId));
      if (allLayersReady) {
        setupMapEventHandlers(mapInstance);
      } else {
        setTimeout(checkAndSetupEvents, 100);
      }
    };

    // Wait a frame to ensure layers are fully registered
    setTimeout(checkAndSetupEvents, 50);
  };

  const createMap = (features) => {
    try {
      let mapInstance = getValidMap();
      if (mapInstance) return mapInstance;

      map.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: mapboxStyleURL,
        center: viewRegion.center,
        zoom: viewRegion.zoom,
        projection: mapboxStyle.includes('Globe') ? 'globe' : 'mercator',
        testMode: true // need to make this configurable
      });

      // Override the _updateContainerDimensions method to use getComputedStyle dimensions.
      // This fixes the issue where map does not take the whole container size when CSS transforms are applied.
      map.current._updateContainerDimensions = function () {
        if (!this._container) return;

        // Use getBoundingClientRect to get the actual rendered dimensions
        const rect = this._container.getBoundingClientRect();
        const width = rect.width || 400;
        const height = rect.height || 300;

        let computedWidth;
        let computedHeight;
        let transformScaleWidth;
        let transformScaleHeight;
        let el = this._container;

        try {
          const computedStyle = window.getComputedStyle(this._container);
          computedWidth = parseInt(computedStyle.width, 10) || 400;
          computedHeight = parseInt(computedStyle.height, 10) || 300;
        } catch (error) {
          console.warn('Fallback to getBoundingClientRect:', error);
        }

        // Walk up the DOM to find any CSS transforms that might affect scaling
        while (el && !computedWidth && (!transformScaleWidth || !transformScaleHeight)) {
          const transformMatrix = window.getComputedStyle(el).transform;
          if (transformMatrix && transformMatrix !== 'none') {
            try {
              const transformValues = transformMatrix.match(/matrix.*\((.+)\)/)[1].split(', ');
              if (transformValues[0] && transformValues[0] !== '0' && transformValues[0] !== '1') {
                transformScaleWidth = transformValues[0];
              }
              if (transformValues[3] && transformValues[3] !== '0' && transformValues[3] !== '1') {
                transformScaleHeight = transformValues[3];
              }
            } catch (e) {
              console.warn('Error parsing CSS transform:', e);
            }
          }
          el = el.parentElement;
        }

        this._containerWidth = computedWidth || (transformScaleWidth ? Math.abs(width / Number(transformScaleWidth)) : width);
        this._containerHeight = computedHeight || (transformScaleHeight ? Math.abs(height / Number(transformScaleHeight)) : height);
      };

      mapInstance = map.current;

      // Force a resize after creation to ensure computed style dimensions are used from the start
      setTimeout(() => {
        if (mapInstance && !mapInstance?._removed) {
          mapInstance?.resize();
        }
      }, 0);

      mapInstance?.on('load', () => {
        if (!mapInstance?.isStyleLoaded()) {
          mapInstance?.once('styledata', () => initializeMap(mapInstance, features));
        } else {
          initializeMap(mapInstance, features);
        }
      });

      mapInstance?.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-left');

      return mapInstance;
    } catch (error) {
      console.error('Error creating map:', error);
      return null;
    }
  };

  const updateMap = (features) => {
    const mapInstance = getValidMap();
    if (!mapInstance || features.length === 0) return;

    if (!mapInstance?.isStyleLoaded()) {
      mapInstance?.once('styledata', () => updateMap(features));
      return;
    }

    addPointSource(mapInstance, pointsRef.current);

    addNewPointSource(mapInstance, features);

    playSound();

    if (!mapInstance?.getSource('newPoint') || !mapInstance?.getSource('point')) return;

    const latestPoint = features[0];
    mapInstance?.getSource('newPoint').setData({
      type: 'FeatureCollection',
      features: [latestPoint]
    });

    const existingPoints = pointsRef.current;
    const newPoints = features;
    const allPoints = oneUoneVRef.current ? [...newPoints] : [...existingPoints, ...newPoints];

    mapInstance?.getSource('point').setData({
      type: 'FeatureCollection',
      features: allPoints
    });

    const places = groupBy(allPoints, ({ properties }) => properties.place);
    const latestPlace = allPoints[allPoints.length - 1]?.properties?.place;

    renderMarkers(mapInstance, places, latestPlace);
    queuePlaceDetailsPrefetch(features);

    pointsRef.current = oneUoneVRef.current ? [...features] : [...pointsRef.current, ...features];
    newPointsRef.current = features;
    setPoint([...pointsRef.current]);
  };

  const setMap = (features) => {
    try {
      if (features) {
        const mapInstance = getValidMap();
        if (!mapInstance) return;

        if (!mapInstance?.getSource('point')) {
          setTimeout(() => {
            setMap(features);
          }, 500);
          return;
        }

        if (features && features.length && mapInstance?.getSource('point')) {
          addNewPointSource(mapInstance, features);

          mapInstance?.getSource('point').setData({
            type: 'FeatureCollection',
            features
          });

          mapInstance?.getSource('newPoint').setData({
            type: 'FeatureCollection',
            features
          });

          pointsRef.current = features;
          newPointsRef.current = features;
          setPoint([...pointsRef.current]);
          queuePlaceDetailsPrefetch(features);
        }
      }
    } catch (error) {
      console.error('Error in setMap:', error);
    }
  };

  useEffect(() => {
    let features = mapData?.features?.length ? mapData.features : currentRosResult[0]?.features || [];
    const mapInstance = createMap(features);

    pointsRef.current = [];
    newPointsRef.current = [];
    setPoint([]);

    setMap(features);

    return () => {
      removeAllMarkers();
      if (placeDetailsPrefetchTimerRef.current) {
        clearTimeout(placeDetailsPrefetchTimerRef.current);
        placeDetailsPrefetchTimerRef.current = null;
      }
      placeDetailsPrefetchQueueRef.current = [];
      if (!mapInstance) return;
      mapInstance?.remove();
    };
  }, [mapId]);

  const addPointSource = (mapInstance, points) => {
    if (mapInstance?.getSource('point')) {
      return;
    }
    mapInstance?.addSource('point', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: points || []
      },
      cluster: true,
      clusterMaxZoom: 4,
      clusterRadius: 50,
    });
  };

  const addNewPointSource = (mapInstance, newPoints) => {
    if (mapInstance?.getSource('newPoint')) {
      return;
    }
    mapInstance?.addSource('newPoint', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: newPoints || []
      }
    });
  };

  const addClustersLayer = (mapInstance) => {
    if (mapInstance?.getLayer('clusters')) {
      return;
    }
    mapInstance?.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'point',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': hexToRGB(colors?.smallCircleColor || '#FF0064', 0.4),
        'circle-radius': 40,
        'circle-opacity': 0
      }
    });
  };

  const addClusterCountLayer = (mapInstance) => {
    if (mapInstance?.getLayer('cluster-count')) {
      return;
    }
    mapInstance?.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'point',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      }
    });
  };

  const addUnclusteredPointLayer = (mapInstance) => {
    if (mapInstance?.getLayer('unclustered-point')) {
      return;
    }
    mapInstance?.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'point',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': colors?.individualPointColor || '#FF0064',
        'circle-radius': 10
      }
    });

    /**
    * Invisible hit detection layer
    * - Exists purely to improve usability by creating a larger clickable area
    * - Positioned exactly under the visible points but with 4x the radius
    * - Zero opacity means it doesn't affect visual design
    */

    mapInstance?.addLayer({
      id: 'unclustered-point-hitarea',
      type: 'circle',
      source: 'point',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': 40,
        'circle-opacity': 0
      }
    });
  };

  const addNewPointsLayer = (mapInstance) => {
    if (mapInstance?.getLayer('newpoints')) {
      return;
    }
    mapInstance?.addLayer({
      id: 'newpoints',
      type: 'circle',
      source: 'newPoint',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': colors?.latestPointColor || '#9567F9',
        'circle-radius': 10
      }
    });
  };

  const cleanupMapEventHandlers = (mapInstance) => {
    if (!mapInstance) {
      eventHandlersRef.current = [];
      return;
    }
    eventHandlersRef.current.forEach(({ event, layer, handler }) => {
      if (layer) {
        mapInstance.off(event, layer, handler);
      } else {
        mapInstance.off(event, handler);
      }
    });
    eventHandlersRef.current = [];
  };

  const LAYERS_TO_REMOVE = [
    'points',
    'clusters',
    // 'cluster-count',
    'unclustered-point',
    'unclustered-point-hitarea',
    'newpoints'
  ];

  const SOURCES_TO_REMOVE = [
    'point',
    'newPoint'
  ];

  const cleanupMapLayersAndSources = (mapInstance) => {
    LAYERS_TO_REMOVE.forEach(layerId => {
      if (mapInstance?.getLayer(layerId)) {
        mapInstance?.removeLayer(layerId);
      }
    });
    SOURCES_TO_REMOVE.forEach(sourceId => {
      if (mapInstance?.getSource(sourceId)) {
        mapInstance?.removeSource(sourceId);
      }
    });
  };

  const applyMapLayers = (mapInstance) => {
    if (!mapInstance) return;
    addPointSource(mapInstance, pointsRef.current);
    addClustersLayer(mapInstance);
    // addClusterCountLayer(mapInstance);
    addUnclusteredPointLayer(mapInstance);
    addNewPointSource(mapInstance, newPointsRef.current);
    addNewPointsLayer(mapInstance);

    // Ensure all layers are ready before setting up event handlers
    const checkAndSetupEvents = () => {
      const allLayersReady = LAYERS_TO_CHECK.every(layerId => mapInstance?.getLayer(layerId));
      if (allLayersReady) {
        setupMapEventHandlers(mapInstance);
      } else {
        setTimeout(checkAndSetupEvents, 100);
      }
    };

    // Wait a frame to ensure layers are fully registered after style change
    setTimeout(checkAndSetupEvents, 50);
  };

  const updateMapSourcesAndLayers = () => {
    const mapInstance = getValidMap();
    if (!mapInstance) return;

    cleanupMapEventHandlers(mapInstance);
    cleanupMapLayersAndSources(mapInstance);

    const projectionType = mapboxStyle.includes('Globe') ? 'globe' : 'mercator';
    mapInstance?.setProjection(projectionType);
    mapInstance?.setStyle(mapboxStyleURL);

    mapInstance?.once('idle', () => {
      applyMapLayers(mapInstance);
    });
  };

  useEffect(() => {
    updateMapSourcesAndLayers();
  }, [mapboxStyle]);

  useEffect(() => {
    const mapInstance = getValidMap();
    if (!mapInstance) return;
    paintMapLayers(mapInstance);
  }, [colors?.mapWaterColor, colors?.mapLandColor]);

  const fetchAndCachePlaceDetails = async (place, featureGroup) => {
    const placeType = featureGroup[0].properties.placeType;
    const username = placeInfo?.current[place]?.username ?? featureGroup[0].properties.username;
    const allCommenters = [...new Set(featureGroup.map(f => f.properties.username))];
    const otherCommenters = allCommenters.filter(user => user !== username);

    const cached = placeInfo?.current[place];
    // Re-fetch when the summary is missing — the summary is generated when
    // the popup is first opened (see loadSummaryIntoPopup) and may land after
    // the first click; the re-fetch is a cheap cache hit on the backend.
    if (!cached || cached.isError || !cached.placeDetails?.summary) {
      try {
        const data = await getMapPopupDetails(place, placeType);
        // Weather is optional (OpenWeather can rate-limit): only treat the
        // response as an error when the place lookup itself failed, otherwise
        // a weather hiccup blanks the photos too.
        const isError = !data.status || !data.entity?.displayName;

        const photoUrls = await Promise.all(
          (data.entity.photos || []).map(photo => {
            return `${process.env.NEXT_PUBLIC_API_URL}image-proxy?url=${encodeURIComponent(photo.split('&key=')[0])}`;
          })
        );

        placeInfo.current[place] = {
          placeName: place,
          placeType,
          username,
          otherCommenters,
          placeDetails: isError ? {} : { ...data.entity, photos: photoUrls },
          isError
        };
      } catch (err) {
        placeInfo.current[place] = { isError: true };
      }
    } else {
      placeInfo.current[place].otherCommenters = otherCommenters;
    }
  };

  const removeAllMarkers = () => {
    markersRef.current.forEach(({ marker, root }) => {
      try { root.unmount(); } catch (_) { /* noop */ }
      try { marker.remove(); } catch (_) { /* noop */ }
    });
    markersRef.current.clear();
  };

  const renderMarkers = (mapInstance, places, latestPlace) => {
    const incoming = new Set(Object.keys(places));

    markersRef.current.forEach((entry, place) => {
      if (!incoming.has(place)) {
        try { entry.root.unmount(); } catch (_) { /* noop */ }
        try { entry.marker.remove(); } catch (_) { /* noop */ }
        markersRef.current.delete(place);
      }
    });

    Object.entries(places).forEach(([place, featureGroup]) => {
      const { coordinates } = featureGroup[0].geometry;

      if (!coordinates || coordinates.length < 2 ||
        typeof coordinates[0] !== 'number' || typeof coordinates[1] !== 'number') {
        console.warn(`Invalid coordinates for place ${place}:`, coordinates);
        return;
      }

      const uniqueUsers = new Set(featureGroup.map(({ properties }) => properties.username));
      const totalUniqueUsers = uniqueUsers.size;

      const markerColor = place === latestPlace
        ? parseColorToHex(colors?.latestPointColor || '#9567F9')
        : parseColorToHex(colors?.individualPointColor || '#FF0064');

      const existing = markersRef.current.get(place);
      if (existing) {
        const { props } = existing;
        if (props.totalUniqueUsers !== totalUniqueUsers || props.markerColor !== markerColor) {
          existing.root.render(
            <CustomMapMarker
              feature={featureGroup[0]}
              totalUniqueUsers={totalUniqueUsers}
              markerColor={markerColor}
            />
          );
          existing.props = { totalUniqueUsers, markerColor };
        }
        return;
      }

      const customMarker = document.createElement('div');
      // Let clicks pass through to map layers (clusters / unclustered-point)
      // so marker DOM never absorbs the click.
      customMarker.style.pointerEvents = 'none';
      const root = ReactDOM.createRoot(customMarker);
      root.render(
        <CustomMapMarker
          feature={featureGroup[0]}
          totalUniqueUsers={totalUniqueUsers}
          markerColor={markerColor}
        />
      );

      const marker = new mapboxgl.Marker(customMarker)
        .setLngLat([coordinates[0], coordinates[1]])
        .addTo(mapInstance);

      markersRef.current.set(place, {
        marker,
        root,
        props: { totalUniqueUsers, markerColor },
      });
    });
  };

  useEffect(() => {
    const mapInstance = getValidMap();
    const allPoints = pointsRef.current;

    if (!mapInstance || !mapInstance?.getSource('point') || allPoints.length === 0) return;

    const places = groupBy(allPoints, ({ properties }) => properties.place);
    const latestPlace = allPoints[allPoints.length - 1]?.properties?.place;

    renderMarkers(mapInstance, places, latestPlace);
  }, [colors?.latestPointColor, colors?.individualPointColor]);

  // useEffect(() => {
  //   const mapInstance = map.current;
  //   if (!mapInstance) return;

  //   if (mapInstance?.getLayer('clusters')) {
  //     mapInstance?.setPaintProperty('clusters', 'circle-color', hexToRGB(colors?.smallCircleColor || '#FF0064', 0.4));
  //   }
  // }, [colors?.smallCircleColor]);

  useEffect(() => {
    oneUoneVRef.current = settings?.mapConfig?.oneUoneV ?? false;
  }, [settings]);

  useEffect(() => {
    if (mapBox.type === 'viewRegion') {
      const mapInstance = getValidMap();
      mapInstance?.jumpTo(mapBox.viewRegion);
      return;
    }
    if (mapBox.type === 'fitBounds') {
      const mapInstance = getValidMap();
      mapInstance?.fitBounds(mapBox.bounds, {
        padding: mapBox.padding || 50,
        duration: 1000 // Smooth animation
      });
      return;
    }
    if (mapBox.type === 'update') { updateMap(mapBox.features); return; }
    if (mapBox.type === 'set') { setMap(mapBox.features); return; }
    if (mapBox.type === 'clean') { cleanUp(); return; }
    cleanUp();
  }, [mapBox]);

  useEffect(() => {
    point?.length > 0 && pushDataLayerForEvent('populate_comments_magic_map');
  }, [point?.length > 0]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      const currentMap = getValidMap();
      if (currentMap) {
        currentMap.resize();
      }
    });

    if (mapContainerRef.current) {
      resizeObserver.observe(mapContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      className={'transition-all duration-300'}
      style={{
        height: mapHeight,
        minHeight: '300px',
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8
      }}
      ref={mapContainerRef}
    />
  );
};

export default MapBoxComponent;
